import { useReducer, useRef } from 'react'
import FocusLock from 'react-focus-lock'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'motion/react'

import { CardContent } from 'js/components/Card'
import { H3 } from 'js/components/Heading'
import Modal from 'js/components/Modal/Modal'
import StickyProvider from 'js/components/Sticky/StickyProvider'
import { useDesktopXlUp } from 'js/lib/helpers/useMediaQuery'
import { Widget as WidgetEditingType } from 'js/page/PageEditor/PageEditor.types'
import Widget from 'js/page/Widget/Widget'

import ActionsElement from './components/ActionsElement'
import {
    widgetOptionsInitialState,
    widgetOptionsReducer,
} from './widgetOptionsReducer'
import styles from './WidgetSettings.module.css'

export type WidgetOptions = {
    saveButton?: {
        disabled?: boolean
    }
}

type WidgetSettingsType = {
    site: any
    widgetEditing: WidgetEditingType
    group: any
    onClose: () => void
    onSave: (settings: any) => void
}

const WidgetSettings = ({
    site,
    widgetEditing,
    group,
    onClose,
    onSave,
}: WidgetSettingsType) => {
    const { t } = useTranslation()

    const refWidget = useRef(null)

    const [widgetOptions, dispatchWidgetOptions] = useReducer(
        widgetOptionsReducer,
        widgetOptionsInitialState,
    )

    const widget = widgetEditing?.widget
    const type = widget?.type
    const typeKeyOverrides = {
        activity: 'feed',
        callToAction: 'call-to-action',
        html: 'code',
        linkList: 'linklist',
        create: 'create-content',
        externalSearch: 'external-search',
        externalSearchChat: 'ai-chat',
        users: 'users',
    }

    const title = t(
        `widget-${typeKeyOverrides[type as keyof typeof typeKeyOverrides] || type}.title`,
    )

    const handleSave = () => {
        refWidget.current.onSave()
    }

    const guid = `${type}-edit`

    const WidgetElement = widgetEditing && (
        <Widget
            ref={refWidget}
            guid={guid}
            site={site}
            entity={widget}
            columnWidth={widgetEditing?.columnWidth}
            isEditing
            group={group}
            onSave={onSave}
            dispatchWidgetOptions={dispatchWidgetOptions}
        />
    )

    const showSidebar = useDesktopXlUp()

    if (!showSidebar) {
        return (
            <Modal
                isVisible={!!widgetEditing}
                title={title}
                size="normal"
                maxWidth="600px"
                onClose={onClose}
                containHeight={type === 'text'} // For now only text widgets are scrollable (overflow-y: auto created problems with select boxes)
                noPadding
            >
                <StickyProvider
                    id={guid}
                    className={styles.contentWrapper}
                    style={{
                        marginTop: '16px',
                        paddingTop: '4px',
                    }}
                >
                    <CardContent style={{ paddingTop: 0 }}>
                        {WidgetElement}
                    </CardContent>
                    {widgetEditing && (
                        <ActionsElement
                            onClose={onClose}
                            handleSave={handleSave}
                            widgetOptions={widgetOptions}
                        />
                    )}
                </StickyProvider>
            </Modal>
        )
    }

    return (
        <AnimatePresence>
            {!!widgetEditing && (
                <FocusLock returnFocus persistentFocus={false}>
                    <motion.div
                        className={styles.wrapper}
                        initial={{ opacity: 0, translateX: '-100%' }}
                        animate={{ opacity: 1, translateX: 0 }}
                        exit={{ opacity: 0, translateX: '-100%' }}
                        transition={{ duration: 0.25 }}
                    >
                        <StickyProvider
                            id={guid}
                            className={styles.contentWrapper}
                        >
                            <CardContent>
                                <H3 className="WidgetSettingsTitle">{title}</H3>
                                {WidgetElement}
                            </CardContent>
                            {widgetEditing && (
                                <ActionsElement
                                    onClose={onClose}
                                    handleSave={handleSave}
                                    widgetOptions={widgetOptions}
                                />
                            )}
                        </StickyProvider>
                    </motion.div>
                </FocusLock>
            )}
        </AnimatePresence>
    )
}

export default WidgetSettings
